import {
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
} from '@paywithextend/components'
import React, { useEffect } from 'react'
import AmexAccountVerificationActionRequiredInnerContent from 'src/components/AmexAccountVerificationActionRequiredBox/AmexAccountVerificationActionRequiredInnerContent'
import AppName from 'src/components/AppName/AppName'
import Box from 'src/components/Box/Box'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import SvgStatusSummaryActionRequiredRed from 'src/components/Svgs/SvgStatusSummaryActionRequiredRed'
import { AmexCardHolderTypes, AmexCardTypes, IssuerCodes } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { accountOverviewUrl } from 'src/navigation/urls'
import { getCreditCardProperties } from 'src/redux/actions/data/creditCards.actions'
import { css } from 'styled-components'

interface AmexAccountVerificationActionRequiredBoxProps {
  creditCard: CreditCardListModel
}

const AmexAccountVerificationActionRequiredBox: React.FC<
  AmexAccountVerificationActionRequiredBoxProps
> = (props): React.ReactElement => {
  const translate = useTranslate()

  const dispatch = useBackgroundDispatch()

  useEffect((): void => {
    dispatch(getCreditCardProperties(props.creditCard.id))
  }, [])

  const innerContent = (
    creditCard: CreditCardListModel,
  ): React.ReactElement => {
    const cardProperties = creditCard.cardProperties
    const celWhitelisted = creditCard.issuer === IssuerCodes.AmexCel

    const emailDeliveryMessage = translate(
      'This email may take up to 10 minutes to be delivered',
    )
    const dontKnowYourAmexLoginMessage = translate(
      "Don't know your Amex login? ${clickHere} to recover your credentials",
      {
        clickHere: (
          <HoverableLink
            size={HoverableLinkSizes.Small}
            underline
            regular
            href={
              'https://www.americanexpress.com/en-us/account/userid/recover'
            }
            ariaLabel={'Click here'}
          >
            {translate('Click here')}
          </HoverableLink>
        ),
      },
    )

    const justAFewMoreStepsMessage = translate(
      'Just a few more steps to complete account registration:',
    )
    const goBackToApp = (key: number): React.ReactElement => {
      return (
        <React.Fragment key={key}>
          {translate(
            "${goBackToApp} - you're now ready to create virtual cards!",
            {
              goBackToApp: (
                <b>
                  {translate('Go back to ${appName}', {
                    appName: <AppName />,
                  })}
                </b>
              ),
            },
          )}
        </React.Fragment>
      )
    }

    if (celWhitelisted) {
      return (
        <AmexAccountVerificationActionRequiredInnerContent
          subtitle={justAFewMoreStepsMessage}
          steps={[
            <React.Fragment key={0}>
              {translate(
                '${checkYourInbox} for a verification email from ${appName}.',
                {
                  checkYourInbox: <b>{translate('Check your inbox')}</b>,
                  appName: <AppName />,
                },
              )}
            </React.Fragment>,
            <React.Fragment key={1}>
              {translate(
                '${clickTheVerificationLink}. Follow the link in the email to verify your registration with American Express.',
                {
                  clickTheVerificationLink: (
                    <b>{translate('Click the verification link')}</b>
                  ),
                },
              )}
            </React.Fragment>,
            goBackToApp(2),
          ]}
        />
      )
    } else {
      if (
        cardProperties?.cardType === AmexCardTypes.Business &&
        cardProperties?.cardHolderType === AmexCardHolderTypes.Secondary
      ) {
        return (
          <AmexAccountVerificationActionRequiredInnerContent
            subtitle={
              <>
                <div
                  css={css`
                    margin-bottom: 20px;
                  `}
                >
                  {translate(
                    'The primary cardholder has received an email from American Express to complete verification of your card.',
                  )}
                </div>
                {translate('Ask them to follow the steps below:')}
              </>
            }
            steps={[
              <React.Fragment key={0}>
                {translate(
                  '${checkTheirInbox} for a verification email from American Express.',
                  {
                    checkTheirInbox: <b>{translate('Check their inbox')}</b>,
                  },
                )}
              </React.Fragment>,
              <React.Fragment key={1}>
                {translate(
                  '${clickTheVerificationLink}. The primary cardholder should follow the link in the email to log into American Express and verify your account.',
                  {
                    clickTheVerificationLink: (
                      <b>{translate('Click the verification link')}</b>
                    ),
                  },
                )}
              </React.Fragment>,
            ]}
            endMessage={translate(
              'Once verified, you can start creating virtual cards in ${appName}!',
              {
                appName: <AppName />,
              },
            )}
            takeNoteBullets={[
              <div key={0}>{emailDeliveryMessage}</div>,
              <div key={1}>
                {translate(
                  'Visit our ${resourceCenter} for detailed instructions',
                  {
                    resourceCenter: (
                      <ResourceCenterLink
                        size={HoverableLinkSizes.Small}
                        underline
                        regular
                        analytics={{
                          event:
                            'account detail - alert component helpcenter link - text link press',
                          properties: {
                            interaction: 'text link press',
                            intent: 'alert component helpcenter link',
                            screen: accountOverviewUrl(':id'),
                          },
                        }}
                      />
                    ),
                  },
                )}
              </div>,
            ]}
          />
        )
      } else if (
        cardProperties?.cardType === AmexCardTypes.Corporate ||
        (cardProperties?.cardType === AmexCardTypes.Business &&
          cardProperties?.cardHolderType === AmexCardHolderTypes.Primary)
      ) {
        return (
          <AmexAccountVerificationActionRequiredInnerContent
            subtitle={justAFewMoreStepsMessage}
            steps={[
              <React.Fragment key={0}>
                {translate(
                  '${checkYourInbox} for a verification email from American Express.',
                  {
                    checkYourInbox: <b>{translate('Check your inbox')}</b>,
                  },
                )}
              </React.Fragment>,
              <React.Fragment key={1}>
                {translate(
                  '${clickTheVerificationLink}. Follow the link in the email to log into American Express and verify your account.',
                  {
                    clickTheVerificationLink: (
                      <b>{translate('Click the verification link')}</b>
                    ),
                  },
                )}
              </React.Fragment>,
              goBackToApp(2),
            ]}
            takeNoteBullets={[
              <div key={0}>{emailDeliveryMessage}</div>,
              <div key={1}>{dontKnowYourAmexLoginMessage}</div>,
              <div key={2}>
                {translate(
                  'Visit our ${resourceCenter} for detailed instructions',
                  {
                    resourceCenter: (
                      <ResourceCenterLink
                        size={HoverableLinkSizes.Small}
                        underline
                        regular
                        analytics={{
                          event:
                            'account detail - alert component helpcenter link - text link press',
                          properties: {
                            interaction: 'text link press',
                            intent: 'alert component helpcenter link',
                            screen: accountOverviewUrl(':id'),
                          },
                        }}
                      />
                    ),
                  },
                )}
              </div>,
            ]}
          />
        )
      } else {
        return (
          <AmexAccountVerificationActionRequiredInnerContent
            subtitle={translate(
              "Before you can use your Amex to create virtual cards in ${appName}, there's a few more steps to complete:",
              {
                appName: <AppName />,
              },
            )}
            steps={[
              <React.Fragment key={0}>
                {translate(
                  "${findThePrimaryCardholder}. If you set up your company's Amex card program, this is likely you. Only the primary cardholder can complete verification.",
                  {
                    findThePrimaryCardholder: (
                      <b>{translate('Find the primary cardholder')}</b>
                    ),
                  },
                )}
              </React.Fragment>,
              <React.Fragment key={1}>
                {translate(
                  '${checkEmail}. The primary cardholder should check their inbox for a verification email from Amex.',
                  {
                    checkEmail: <b>{translate('Check email')}</b>,
                  },
                )}
              </React.Fragment>,
              <React.Fragment key={2}>
                {translate(
                  '${clickTheVerificationLink}. The primary cardholder should follow the link in the email to verify your registration with Amex.',
                  {
                    clickTheVerificationLink: (
                      <b>{translate('Click the verification link')}</b>
                    ),
                  },
                )}
              </React.Fragment>,
            ]}
            takeNoteBullets={[
              <div key={0}>{emailDeliveryMessage}</div>,
              <div key={1}>{dontKnowYourAmexLoginMessage}</div>,
              <div key={2}>
                {translate(
                  'Not sure how to determine the primary cardholder? View our ${resourceCenter} for detailed instructions',
                  {
                    resourceCenter: (
                      <ResourceCenterLink
                        size={HoverableLinkSizes.Small}
                        underline
                        regular
                        analytics={{
                          event:
                            'account detail - alert component helpcenter link - text link press',
                          properties: {
                            interaction: 'text link press',
                            intent: 'alert component helpcenter link',
                            screen: accountOverviewUrl(':id'),
                          },
                        }}
                      />
                    ),
                  },
                )}
              </div>,
            ]}
          />
        )
      }
    }
  }

  return (
    <Box
      css={css`
        align-items: center;
        align-self: center;
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 500px;
      `}
    >
      <SvgStatusSummaryActionRequiredRed />
      <div
        css={css`
          display: flex;
          align-items: center;
          margin-top: 10px;
          ${getFontCss(
            16,
            styleConstants.colors.red_dark,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        <h3>{translate('Action Required')}</h3>
      </div>
      <div>{innerContent(props.creditCard)}</div>
    </Box>
  )
}

export default AmexAccountVerificationActionRequiredBox
